import React from "react";

const baseURL = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000";

// Resolve stored image paths the same way the campaign page does.
const normalizeImageUrl = (url) => {
  if (!url) return "/images/placeholder.jpg";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${baseURL}${url}`;
  return `${baseURL}/${url}`;
};

// USD and POL are shown as separate figures — never summed into one number.
const priceLabel = (item) => {
  const parts = [];
  if (item.priceUsd != null) parts.push(`$${Number(item.priceUsd).toLocaleString()}`);
  if (item.pricePol != null) parts.push(`${Number(item.pricePol).toLocaleString()} POL`);
  return parts.join(" / ");
};

const RailBadge = ({ rail }) => (
  <span
    className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
      rail === "onchain"
        ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200"
        : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200"
    }`}
  >
    {rail === "onchain" ? "On-chain" : "Off-chain"}
  </span>
);

const ArtCard = ({ item, showPrice }) => {
  const price = showPrice ? priceLabel(item) : "";
  return (
    <div className="bg-white dark:bg-jacarta-700 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden w-full flex flex-col">
      <div className="w-full aspect-square relative overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-jacarta-600">
        <img
          src={normalizeImageUrl(item.image)}
          alt={item.name}
          className="w-full h-full object-contain transition-transform hover:scale-105"
        />
      </div>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="text-sm sm:text-base font-semibold text-gray-700 dark:text-white line-clamp-2 leading-tight">
            {item.name}
          </h3>
          <RailBadge rail={item.rail} />
        </div>
        {item.creator && (
          <p className="text-xs sm:text-sm text-gray-600 dark:text-jacarta-300 truncate mb-1">
            by {item.creator}
          </p>
        )}
        {item.description && (
          <p className="text-xs sm:text-sm text-gray-600 dark:text-jacarta-300 line-clamp-2 mb-2">
            {item.description}
          </p>
        )}
        {price && (
          <div className="mt-auto text-base sm:text-lg font-semibold text-gray-700 dark:text-white">
            {price}
          </div>
        )}
      </div>
    </div>
  );
};

const Grid = ({ items, showPrice }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
    {items.map((item) => (
      <ArtCard key={`${item.rail}-${item.id}`} item={item} showPrice={showPrice} />
    ))}
  </div>
);

// Impact art tied to a campaign: pieces listed for it, and pieces gifted to it.
// Both rails (off-chain DB assets and on-chain registered tokens) are shown,
// each tagged honestly.
const CampaignArts = ({ arts = [], giftedArts = [] }) => {
  if (!arts.length && !giftedArts.length) return null;

  return (
    <section className="pt-2 pb-10">
      <div className="container mx-auto px-3 sm:px-4">
        {arts.length > 0 && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-4 dark:text-white">Impact Art</h2>
            <Grid items={arts} showPrice />
          </div>
        )}
        {giftedArts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4 dark:text-white">
              Gifted to this campaign
            </h2>
            <Grid items={giftedArts} showPrice={false} />
          </div>
        )}
      </div>
    </section>
  );
};

export default CampaignArts;
